import { useState } from 'react';
import { RefreshCw, Monitor, Terminal, Wifi, WifiOff, Loader2, CornerLeftUp, KeyRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDroppable } from '@dnd-kit/core';
import clsx from 'clsx';
import { openRemoteDisplayWindow } from '../../../utils/openRemoteDisplayWindow';
import { openHostConsoleWindow } from '../../../utils/openHostConsoleWindow';
import { openRouterManagementWindow } from '../../../utils/openRouterManagementWindow';
import { openRouterConsoleWindow } from '../../../utils/openRouterConsoleWindow';
import { formatDate, formatDateTime } from '../../../utils/format';
import { normalizeTypeSlug, HOSTS_TYPE_SLUG } from '../typeSlugs';
import DeviceRow from './DeviceRow';
import GroupRow from './GroupRow';
import TempAccessModal from './TempAccessModal';
import { useTempAccessesByDevice } from '../../../hooks/useTempAccess';
import { supportsTempAccess } from '../../../services/tempAccessApi';

const apiAuthStatusOrder = ['Unknown', 'Ok', 'AuthFailed', 'Unreachable'];

function normalizeApiAuthStatus(v) {
  if (v === undefined || v === null) return 'Unknown';
  const n = Number(v);
  if (!Number.isNaN(n) && n >= 0 && n < apiAuthStatusOrder.length) {
    return apiAuthStatusOrder[n];
  }
  const s = String(v);
  return apiAuthStatusOrder.includes(s) ? s : 'Unknown';
}

/** Linha "voltar um nível" — também aceita drop para tirar o item do grupo atual. */
function UpRow({ parentGroupId, columnCount, onNavigate, label }) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'group-up',
    data: { type: 'group', groupId: parentGroupId },
  });

  return (
    <tr
      ref={setNodeRef}
      onClick={() => onNavigate(parentGroupId)}
      className={clsx(
        'cursor-pointer select-none bg-gray-50/70 hover:bg-gray-100',
        isOver && 'ring-2 ring-inset ring-theme-primary bg-theme-primary/5'
      )}
    >
      <td className="px-6 py-2" colSpan={columnCount}>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <CornerLeftUp size={16} />
          {label}
        </div>
      </td>
    </tr>
  );
}

/**
 * Lista de devices com navegação por grupos dentro da própria aba.
 *
 * Ordem das linhas: "voltar" → grupos do nível → devices soltos no nível.
 * Com "mostrar tudo" ligado, os grupos somem e a lista fica plana (todos os
 * devices do tipo, estejam em qualquer grupo).
 */
const DeviceList = ({
  devices,
  typeDevices,
  selectedType,
  isLoading,
  onRefresh,
  groups = [],
  showAll = false,
  currentGroupId = null,
  parentGroupId = null,
  expandedGroups,
  onToggleGroup,
  onOpenGroup,
  onEditGroup,
  onDeleteGroup,
  onNavigateGroup,
  onMoveDevice,
  onDeviceContextMenu,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const typeSlug = normalizeTypeSlug(selectedType?.slug);
  const { data: tempAccessByDevice = {} } = useTempAccessesByDevice();
  const [tempAccessDevice, setTempAccessDevice] = useState(null);

  /** Ícone do acesso temporário automais-temp: cinza (nenhum), verde (ativo), âmbar (revogando), vermelho (vulnerável). */
  const renderTempAccessButton = (device) => {
    if (!supportsTempAccess(device)) return null;
    const access = tempAccessByDevice[device.id];
    const status = access?.status;
    const cls =
      status === 'vulnerable'
        ? 'text-red-600 hover:bg-red-100 animate-pulse'
        : status === 'revoking'
          ? 'text-amber-600 hover:bg-amber-50'
          : status === 'active'
            ? 'text-green-600 hover:bg-green-50'
            : 'text-gray-600 hover:bg-gray-50';
    const title =
      status === 'vulnerable'
        ? t('managedDevices.tempAccess.iconVulnerable')
        : status === 'active'
          ? t('managedDevices.tempAccess.iconActive', { when: formatDateTime(access.expiresAt) })
          : status === 'revoking'
            ? t('managedDevices.tempAccess.iconRevoking')
            : t('managedDevices.tempAccess.iconIdle');
    return (
      <button onClick={() => setTempAccessDevice(device)} title={title} className={clsx('p-1 rounded', cls)}>
        <KeyRound size={14} />
      </button>
    );
  };

  const handleOpenDetail = (device) => {
    navigate(`/managed-devices/${device.id}`);
  };

  const handleOpenFirewall = (device) => {
    navigate(`/managed-devices/${device.id}#firewall`);
  };

  const renderAccessActions = (device) => {
    if (typeSlug === HOSTS_TYPE_SLUG) {
      const displayAvailable = device.remoteDisplayEnabled !== false;
      return (
        <>
          {displayAvailable && (
            <button
              onClick={() => openRemoteDisplayWindow(device.id)}
              title={t('managedDevices.openDisplay')}
              className="p-1 text-theme-primary hover:bg-theme-primary/10 rounded"
            >
              <Monitor size={14} />
            </button>
          )}
          <button
            onClick={() => openHostConsoleWindow(device.id)}
            title={t('managedDevices.openConsole')}
            className="p-1 text-theme-primary hover:bg-theme-primary/10 rounded"
          >
            <Terminal size={14} />
          </button>
          {renderTempAccessButton(device)}
        </>
      );
    }

    if (typeSlug === 'router') {
      return (
        <>
          <button
            onClick={() => openRouterManagementWindow(device.id)}
            title={t('managedDevices.openRouterManagement')}
            className="p-1 text-theme-primary hover:bg-theme-primary/10 rounded"
          >
            <Monitor size={14} />
          </button>
          <button
            onClick={() => openRouterConsoleWindow(device.id)}
            title={t('managedDevices.openRouterConsole')}
            className="p-1 text-theme-primary hover:bg-theme-primary/10 rounded"
          >
            <Terminal size={14} />
          </button>
          {renderTempAccessButton(device)}
        </>
      );
    }

    return null;
  };

  // Colunas específicas por tipo
  const renderTypeSpecificColumns = (device) => {
    switch (typeSlug) {
      case HOSTS_TYPE_SLUG:
        return (
          <>
            <td className="px-6 py-4">
              <div className="flex items-center gap-2">
                {device.vpnPeerReachableViaVpn ? (
                  <Wifi size={14} className="text-green-500" title={t('managedDevices.status.vpnConnected')} />
                ) : (
                  <WifiOff size={14} className="text-gray-400" title={t('managedDevices.status.vpnDisconnected')} />
                )}
                <span className="text-sm text-gray-500">{device.vpnIp || 'N/A'}</span>
              </div>
            </td>
            <td className="px-6 py-4">
              <span className="text-sm text-gray-500">
                {device.sshPort ? `SSH:${device.sshPort}` : 'N/A'}
              </span>
            </td>
          </>
        );

      case 'router':
        return (
          <>
            <td className="px-6 py-4">
              <span className="text-sm text-gray-500">{device.model || 'N/A'}</span>
            </td>
            <td className="px-6 py-4">
              <div className="flex items-center gap-2">
                {normalizeApiAuthStatus(device.apiAuthStatus) === 'Ok' ? (
                  <span
                    className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full"
                    title={device.apiAuthMessage || ''}
                  >
                    {t('managedDevices.status.apiConnected')}
                  </span>
                ) : (
                  <span
                    className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full"
                    title={device.apiAuthMessage || ''}
                  >
                    {t('managedDevices.status.apiDisconnected')}
                  </span>
                )}
              </div>
            </td>
          </>
        );

      case 'switch':
      case 'access-point':
        return (
          <>
            <td className="px-6 py-4">
              <span className="text-sm text-gray-500">{device.model || 'N/A'}</span>
            </td>
            <td className="px-6 py-4">
              <div className="flex items-center gap-2">
                {device.latency ? (
                  <>
                    <Wifi size={14} className="text-green-500" />
                    <span className="text-sm text-gray-500">{device.latency}ms</span>
                  </>
                ) : (
                  <span className="text-sm text-gray-500">N/A</span>
                )}
              </div>
            </td>
          </>
        );

      default:
        return (
          <>
            <td className="px-6 py-4">
              <span className="text-sm text-gray-500">{device.vpnIp || 'N/A'}</span>
            </td>
            <td className="px-6 py-4">
              <span className="text-sm text-gray-500">
                {device.lastSeenAt ? formatDate(device.lastSeenAt) : '—'}
              </span>
            </td>
          </>
        );
    }
  };

  const getTypeSpecificHeaders = () => {
    switch (typeSlug) {
      case HOSTS_TYPE_SLUG:
        return [t('managedDevices.headers.vpn'), t('managedDevices.headers.ssh')];
      case 'router':
        return [t('managedDevices.headers.model'), t('managedDevices.headers.apiStatus')];
      case 'switch':
      case 'access-point':
        return [t('managedDevices.headers.model'), t('managedDevices.headers.connection')];
      default:
        return [t('managedDevices.headers.ipAddress'), t('managedDevices.headers.lastSeen')];
    }
  };

  const typeHeaders = getTypeSpecificHeaders();
  const columnCount = 4 + typeHeaders.length;

  const renderDeviceRow = (device, depth = 0) => (
    <DeviceRow
      key={device.id}
      device={device}
      depth={depth}
      t={t}
      onOpen={handleOpenDetail}
      onOpenFirewall={handleOpenFirewall}
      onMove={onMoveDevice}
      onContextMenu={onDeviceContextMenu}
      tempAccess={tempAccessByDevice[device.id]}
      renderTypeCells={renderTypeSpecificColumns}
      renderAccessActions={renderAccessActions}
    />
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-theme-primary" />
        <span className="ml-2 text-gray-600">{t('managedDevices.loadingDevices')}</span>
      </div>
    );
  }

  const visibleGroups = showAll ? [] : groups;
  const isEmpty = visibleGroups.length === 0 && (!devices || devices.length === 0) && !currentGroupId;

  if (isEmpty) {
    return (
      <div className="text-center py-12">
        <div className="mb-4">
          <Monitor className="h-16 w-16 text-gray-400 mx-auto" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {selectedType?.name
            ? t('managedDevices.emptyForType', { type: selectedType.name.toLowerCase() })
            : t('managedDevices.emptyForTypeFallback')}
        </h3>
        <p className="text-gray-500">{t('managedDevices.matchHint')}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center p-6 border-b border-gray-200">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            {selectedType?.name || t('managedDevices.headers.device')} ({devices.length})
          </h3>
          {selectedType?.description && (
            <p className="text-sm text-gray-500 mt-1">{selectedType.description}</p>
          )}
        </div>
        <button
          onClick={onRefresh}
          className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw size={14} />
          {t('managedDevices.refresh')}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('managedDevices.headers.device')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('managedDevices.headers.status')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('managedDevices.headers.kind')}
              </th>
              {typeHeaders.map((header) => (
                <th key={header} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {header}
                </th>
              ))}
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('managedDevices.headers.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {!showAll && currentGroupId && (
              <UpRow
                parentGroupId={parentGroupId}
                columnCount={columnCount}
                onNavigate={onNavigateGroup}
                label={t('managedDevices.groups.up')}
              />
            )}

            {visibleGroups.map((group) => (
              <GroupRow
                key={group.id}
                node={group}
                columnCount={columnCount}
                typeDevices={typeDevices}
                expandedGroups={expandedGroups}
                onToggle={onToggleGroup}
                onOpen={onOpenGroup}
                onEdit={onEditGroup}
                onDelete={onDeleteGroup}
                renderDeviceRow={renderDeviceRow}
                t={t}
              />
            ))}

            {devices.map((device) => renderDeviceRow(device))}

            {visibleGroups.length === 0 && devices.length === 0 && (
              <tr>
                <td colSpan={columnCount} className="px-6 py-8 text-center text-sm text-gray-500">
                  {t('managedDevices.matchHint')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {tempAccessDevice && (
        <TempAccessModal
          device={tempAccessDevice}
          current={tempAccessByDevice[tempAccessDevice.id]}
          onClose={() => setTempAccessDevice(null)}
        />
      )}
    </div>
  );
};

export default DeviceList;
